// Column model + pure helpers for the Onboarding Support board.
//
// Columns are DERIVED, never stored: Intake = new leads with no active run
// (server-scoped); day columns come from run.currentDay (server-computed
// local date math); Contact Made = run.state. No mover job anywhere.

// stepIsClosed lives in slaUtils (the clock module) and mirrors
// server/lib/workCalendar.js `workableUntil`. It is load-bearing, not
// cosmetic: the server refuses a step past its closing time, so if the two
// drift the board offers work that 409s.
import { stepTone, stepIsClosed } from './slaUtils';
import { SOURCE_LABEL, fill, isUmbrella, pickWording, sourceNameOf, sourceOf, sourceValues } from '../../../lib/messageCore';


// The day pipeline (Intake and Contact Made live in the top lanes, not the
// rail). Quiet columns — nothing is ever due there — are collapsible and
// render as slim rails by default so the 7 working columns get all the width.
export const DAY_COLUMNS = [
  { key: 'day1', title: 'Day 1', days: [1] },
  { key: 'day2', title: 'Day 2', days: [2] },
  { key: 'day3', title: 'Day 3', days: [3] },
  {
    key: 'day4',
    title: 'Day 4',
    days: [4],
    quiet: 'Breather',
    collapsible: true,
  },
  { key: 'day5', title: 'Day 5', days: [5] },
  { key: 'day6', title: 'Day 6', days: [6] },
  { key: 'day7', title: 'Day 7', days: [7] },
  {
    key: 'day8_9',
    title: 'Days 8-9',
    days: [8, 9],
    quiet: 'Quiet days',
    collapsible: true,
  },
  { key: 'day10', title: 'Day 10', days: [10], last: true },
];

export const columnKeyForRun = (run) => {
  if (run.state === 'contact_made') return 'contact';
  const day = Math.max(1, run.currentDay || 1);
  if (day >= 10) return 'day10'; // grace days stay parked on Day 10
  if (day >= 8) return 'day8_9';
  return `day${day}`;
};

export const stepsForDay = (steps = [], day) =>
  steps.filter((s) => s.day === day);

export const stepsForToday = (run) =>
  (run.steps || []).filter((s) => s.day === Math.min(run.currentDay, 10));

// "Done for today" — every step scheduled today resolved (nothing pending
// or missed). Shared by the tile's green treatment AND the board's
// hide-done filter. Quiet days (no steps today) are breathers, not "done".
// A step whose closing time has passed counts as resolved: nobody can act on
// it any more, so leaving the tile un-green would be nagging about work the
// board itself refuses to accept.
export const runDoneForToday = (run, workCalendar = null, now = new Date()) => {
  const today = stepsForToday(run);
  return (
    today.length > 0 &&
    today.every(
      (s) =>
        (s.status !== 'pending' && s.status !== 'missed') ||
        stepIsClosed(s, workCalendar, now),
    )
  );
};

// The step the specialist should act on next = the EARLIEST unresolved step
// they can ACTUALLY do. Chain-locked steps are excluded — a locked SMS #1
// whose provisional window lapsed while the chain waits on Call #2 is
// waiting, not late. A breached step stays front and center (red, "Xm over")
// until it is done or its day closes.
//
// The cutoff is the step's CLOSING TIME (Nic 2026-08-15), not the cadence-day
// rollover it used to be. The old `s.day >= currentDay` rule left Friday's
// steps clickable all weekend, because the day counter freezes on non-working
// days, and gave a step whose window shut at 09:00 no closing time of its own.
export const currentStepOf = (
  run,
  cadenceVersion = null,
  workCalendar = null,
  now = new Date(),
) => {
  const actionable = (run.steps || [])
    .filter((s) => s.status === 'pending' || s.status === 'missed')
    .filter((s) => !stepIsClosed(s, workCalendar, now))
    .filter((s) => !cadenceVersion || !chainBlockerOf(s, run, cadenceVersion))
    .sort((a, b) => new Date(a.windowStartAt) - new Date(b.windowStartAt));
  return actionable[0] || null;
};

// Attention sort: within a column, behind (0) beats act-now (1) beats
// upcoming (2) beats quiet (3) — the top of every column is always its
// hottest tile.
export const attentionRank = (
  run,
  now = new Date(),
  cadenceVersion = null,
  workCalendar = null,
) => {
  const step = currentStepOf(run, cadenceVersion, workCalendar, now);
  if (!step) return 3;
  const tone = stepTone(step, now);
  if (tone.state === 'overdue') return 0;
  if (tone.state === 'due') return 1;
  if (tone.state === 'upcoming') return 2;
  return 3;
};

export const sortByAttention = (
  runs,
  now = new Date(),
  cadenceVersion = null,
  workCalendar = null,
) =>
  [...runs].sort((a, b) => {
    const rank =
      attentionRank(a, now, cadenceVersion, workCalendar) -
      attentionRank(b, now, cadenceVersion, workCalendar);
    if (rank !== 0) return rank;
    const stepA = currentStepOf(a, cadenceVersion, workCalendar, now);
    const stepB = currentStepOf(b, cadenceVersion, workCalendar, now);
    return (
      new Date(stepA?.windowStartAt || a.startedAt) -
      new Date(stepB?.windowStartAt || b.startedAt)
    );
  });

export const fullLeadName = (lead) =>
  [lead?.leadFirstName, lead?.leadLastName].filter(Boolean).join(' ') ||
  'Unknown lead';

// The board's one search (Nic 2026-09-28): does this lead match what was
// typed (name, email, or phone digits)? An empty search matches everyone.
export const leadMatchesQuery = (lead, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const digits = q.replace(/\D/g, '');
  return (
    fullLeadName(lead).toLowerCase().includes(q) ||
    String(lead?.leadEmail || '').toLowerCase().includes(q) ||
    (digits.length >= 3 && String(lead?.leadPhone || '').replace(/\D/g, '').includes(digits))
  );
};

// Source identity — THREE channels, three colors (Nic 2026-08-02), truthful
// to how the lead arrived and RETROACTIVE (a referrer who later becomes a
// partner upgrades their old leads' identity at read time):
//  - org:     an organization feeding us leads (Envoy, org-type partners)
//  - partner: an individual who IS a partner (their own link, or a /referral
//             referrer matched to a graduated payee — referrerPartnerName)
//  - person:  a /referral referrer who is NOT (yet) a partner
//  - event:   event channels (own tab; identity kept for safety)
export const sourceIdentity = (lead) => {
  if (!lead) return { channel: 'other', label: '', detail: '' };
  if (lead.leadSourceSystem === 'envoy') {
    return { channel: 'org', label: 'VeteranRelief.org', detail: 'Envoy' };
  }
  if (lead.leadSourceSystem === 'event' || lead.partnerType === 'event') {
    return {
      channel: 'event',
      label: lead.partnerName || lead.leadMeta?.siteName || 'Event',
      detail: 'Event',
    };
  }
  if (lead.leadSourceSystem === 'referral') {
    // The 'referral-program' partner row is PLUMBING, not an identity — a
    // bucket for referrers who haven't signed up. It must never present as
    // an organization (it is typed 'organization' in the DB — ignore that).
    const isProgramUmbrella =
      !lead.partnerReferralUrl ||
      lead.partnerReferralUrl === 'referral-program';
    if (!isProgramUmbrella && lead.partnerName) {
      // A partner's own link. Organizations stay purple; individuals teal.
      return lead.partnerType === 'organization'
        ? { channel: 'org', label: lead.partnerName, detail: 'Organization' }
        : {
            channel: 'partner',
            label: lead.partnerName,
            detail: 'Partner referral',
          };
    }
    if (lead.referrerPartnerName) {
      return {
        channel: 'partner',
        label: lead.referrerPartnerName,
        detail: 'Partner referral',
      };
    }
    const r = lead.leadMeta?.referredBy;
    const referrer =
      r && typeof r === 'object' && (r.firstName || r.lastName)
        ? `${r.firstName || ''} ${r.lastName || ''}`.trim()
        : typeof r === 'string'
          ? r
          : null;
    return {
      channel: 'person',
      label: referrer || 'Referral',
      detail: 'Referral',
    };
  }
  // Partner QR/link scans (2026-08-20): the lead arrived through a verified
  // partner's own QR/landing page — same identity treatment as a partner
  // using their referral link, so the board can tell a real partner apart
  // from a plain person referral.
  if (lead.leadSourceSystem === 'partner_qr' && lead.partnerName) {
    // Belt-and-suspenders (createEventLead rejects the referral-program slug
    // server-side): an umbrella-attributed row must present as a plain
    // referral, never as an organization — returned EXPLICITLY here because
    // falling through would land on the org branch below (the umbrella row
    // is typed 'organization' in the DB).
    if (isUmbrella(lead)) {
      return { channel: 'person', label: 'Referral', detail: 'Referral' };
    }
    return lead.partnerType === 'organization'
      ? { channel: 'org', label: lead.partnerName, detail: 'Organization' }
      : {
          channel: 'partner',
          label: lead.partnerName,
          detail: 'Partner referral',
        };
  }
  if (lead.partnerType === 'organization' && lead.partnerName) {
    return { channel: 'org', label: lead.partnerName, detail: 'Organization' };
  }
  // The website's "have us reach out" form (2026-10-14): a Type like every
  // other source, instead of a bare "Website".
  if (lead.leadSourceSystem === 'reach-out') {
    return {
      channel: 'other',
      label: lead.leadMeta?.siteName || 'Website',
      detail: 'Reach-out form',
    };
  }
  return {
    channel: 'other',
    label:
      lead.leadMeta?.siteName || lead.leadSourceSystem || lead.leadSource || '',
    detail: '',
  };
};

export const sourceLabel = (lead) => {
  const id = sourceIdentity(lead);
  return id.detail && id.detail !== id.label
    ? `${id.label} · ${id.detail}`
    : id.label;
};

// Token rendering for scripts/templates. `[bio]` and `[calendly link]` come
// from the acting user's profile (Phase 4 adds the admin editor for them).
//
// MIRROR of server/lib/cadenceScripts.js. Keep these identical on both sides:
//   renderScript, formatPhoneToken, referralSourceOf, resolveScriptVariant,
//   UNRESOLVED_TOKEN_RE.
// NOT "preview only", despite the old comment here: for a CLICKED send the
// browser posts its final text and the server honours it (it re-renders only
// when body.text is empty), so THIS copy is what a veteran actually receives.
// The server copy is authoritative for automated emails and that fallback.
// Mirroring is forced — server jest cannot see src/, CRA cannot see server/.

// Since 2026-10-22 (Messages) the rules underneath are ONE shared core,
// src/lib/messageCore.js (generated from server/lib/messages/core.js): every
// lead has one source, a message has wording for each, and fill-ins are
// braces ({first name}) with the old [brackets] read forever.

// An unfilled token must never go out. Exported so LeadSlideOver's compose
// gate uses the same literal instead of retyping it (they drifted once).
export const UNRESOLVED_TOKEN_RE =
  /\[(bio|calendly link|assignee number|referral source)\]|\{\s*(bio|owner\s+phone|referral\s+source|partner\s+name|event\s+name|referrer\s+name)\s*\}/i;

/**
 * WHO SENT US THIS LEAD, as words you can say: the partner's or event's name,
 * the referrer's name, "our website" or "your call with our team". Never
 * unknown (every lead has a source); null only with no lead at all. Mirrors
 * cadenceScripts.js.
 */
export const referralSourceOf = (lead) => (lead ? sourceNameOf(sourceOf(lead)) : null);

/**
 * Which wording of a script this lead gets: their partner's or event's own
 * wording, else their source's (Messages). Old scripts with partner variants
 * still resolve the old way. Mirrors cadenceScripts.js.
 *
 * Needs the lead's partner fields and leadMeta, which the board payload ships
 * (leadSummary in onboardingBoardController.js).
 */
export const resolveScriptVariant = (script, lead) => {
  if (script?.message) {
    const source = lead ? sourceOf(lead) : null;
    const picked = pickWording(script.message, source);
    if (picked) {
      const label = picked.key.includes(':') ? `${source.name}'s own` : SOURCE_LABEL[picked.key] || null;
      return { subject: picked.subject || script.subject, body: picked.body, variantId: picked.key, variantLabel: label };
    }
    return { subject: script.subject, body: script.body, variantId: null, variantLabel: null };
  }
  const fallback = {
    subject: script?.subject,
    body: script?.body,
    variantId: null,
    variantLabel: null,
  };
  if (!script || !Array.isArray(script.variants) || !lead?.leadPartnerId) {
    return fallback;
  }
  const hit = script.variants.find(
    (v) => v?.partnerId && String(v.partnerId) === String(lead.leadPartnerId),
  );
  if (!hit) return fallback;
  return {
    subject:
      typeof hit.subject === 'string' && hit.subject.trim()
        ? hit.subject
        : script.subject,
    body: hit.body,
    variantId: hit.variantId || null,
    variantLabel: hit.label || null,
  };
};

// Number tokens render human-readable — (615) 263-9391, never raw +1615...
// Mirrors formatPhoneToken in server/lib/cadenceScripts.js.
export const formatPhoneToken = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const ten =
    digits.length === 10
      ? digits
      : digits.length === 11 && digits.startsWith('1')
        ? digits.slice(1)
        : null;
  if (!ten) return raw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
};

export const renderScript = (template, { lead, user, assigneeNumber } = {}) => {
  if (!template) return '';
  return fill(template, {
    first_name: lead?.leadFirstName || 'there',
    // {my name} = the person this message speaks as (mirrors cadenceScripts.js).
    my_name: user?.userFirstName || '',
    // Unfilled, these stay as typed and the send is blocked.
    bio: user?.userCadenceBio || null,
    owner_phone: formatPhoneToken(assigneeNumber) || null,
    // Their source's name ({partner name} / {event name} / {referrer name}).
    ...sourceValues(lead ? sourceOf(lead) : null),
  });
};

// Scripts come from the board payload's LIVE store (2026-08-11 — the server
// merges live over any pre-cutover embedded version scripts), so previews
// always match what would actually send, even mid-run.
export const scriptForStep = (step, scripts) => {
  if (!step?.scriptKey) return null;
  return scripts?.[step.scriptKey] || null;
};

// Chain gate (client mirror of the server's getChainBlocker): the step row
// blocking this one, or null. Skipped/cancelled dependencies are looked
// THROUGH transitively — an opted-out SMS doesn't unlock the end-of-day
// call ahead of Call #2.
export const chainBlockerOf = (step, run, cadenceVersion) => {
  const defs = cadenceVersion?.definition?.steps || [];
  const stepsByKey = new Map((run?.steps || []).map((s) => [s.stepKey, s]));
  let key = defs.find((d) => d.stepKey === step.stepKey)?.chainAfter;
  const seen = new Set();
  while (key && !seen.has(key)) {
    seen.add(key);
    const dependency = stepsByKey.get(key);
    if (!dependency) return null;
    if (dependency.status === 'pending' || dependency.status === 'missed') {
      return dependency;
    }
    if (dependency.status === 'done') return null;
    key = defs.find((d) => d.stepKey === dependency.stepKey)?.chainAfter;
  }
  return null;
};
